import { useEffect, useState, Suspense, lazy } from "react";
import { motion } from "framer-motion";
import { Link, Route } from "wouter";
// Lazy load heavy components untuk reduce initial bundle size
const HeroIntro = lazy(() => import("@/components/HeroIntro").then(m => ({ default: m.default })));
const SeeMenuSection = lazy(() => import("@/components/SeeMenuSection").then(m => ({ default: m.default })));
const DebugPanel = lazy(() => import("@/components/DebugPanel").then(m => ({ default: m.default })));
const SmoothScroll = lazy(() => import("@/components/SmoothScroll").then(m => ({ default: m.default })));
const ScrollProgress = lazy(() => import("@/components/ScrollProgress").then(m => ({ default: m.default })));
import { LayoutConfigProvider } from "@/lib/layoutConfig";
// Lazy load page routes
const MenuPage = lazy(() => import("@/pages/MenuPage").then(m => ({ default: m.default })));
const AdminPage = lazy(() => import("@/pages/AdminPage").then(m => ({ default: m.default })));

// SmartHeader Component
function SmartHeader() {
  const [dark, setDark] = useState(false);
  const [isTvPhase, setIsTvPhase] = useState(false);

  useEffect(() => {
    let ticking = false;
    
    const checkHeader = () => {
      const lightSections = document.querySelectorAll<HTMLElement>(".light-section");
      const anyLight = Array.from(lightSections).some((el) => {
        const r = el.getBoundingClientRect();
        return r.top < 80 && r.bottom > 80;
      });
      setDark(anyLight);
      ticking = false;
    };
    
    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(checkHeader);
        ticking = true;
      }
    };
    
    const checkTvPhase = () => {
      // Cek apakah HeroIntro sedang di phase awal (sebelum fullscreen)
      const heroElement = document.querySelector('[data-phase]');
      if (heroElement) {
        const phase = heroElement.getAttribute('data-phase');
        // Sembunyikan header saat phase: assemble, logoFull, hexVideo, tv
        const hideHeaderPhases = ['assemble', 'logoFull', 'hexVideo', 'tv'];
        setIsTvPhase(hideHeaderPhases.includes(phase || ''));
      }
    };
    
    window.addEventListener("scroll", onScroll, { passive: true });
    checkHeader();
    checkTvPhase();
    
    // Interval untuk check phase TV secara berkala
    const intervalId = setInterval(checkTvPhase, 500);
    
    return () => {
      window.removeEventListener("scroll", onScroll);
      clearInterval(intervalId);
    };
  }, []);

  const textClass = "text-black"; // Selalu hitam

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <motion.header
      className={`fixed top-5 left-5 right-5 z-50 flex items-center justify-between px-6 md:px-8 py-5 md:py-6 transition-colors duration-500 ${textClass}`}
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: isTvPhase ? 0 : 1, y: isTvPhase ? -20 : 0 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      style={{ pointerEvents: isTvPhase ? "none" : "auto" }}
    >
      <button
        onClick={scrollToTop}
        className="retrorika-font text-sm md:text-base tracking-widest font-extralight opacity-90 hover:opacity-100 transition-opacity text-inherit"
      >
        RETRORIKA
      </button>
      <Link
        to="/menu"
        className="retrorika-font text-sm md:text-base tracking-widest font-extralight opacity-75 hover:opacity-100 transition-opacity duration-300 text-inherit"
      >
        OUR MENU
      </Link>
    </motion.header>
  );
}

// HomePage Component
function HomePage() {
  return (
    <LayoutConfigProvider>
      <SmartHeader />
      <Suspense fallback={<div className="min-h-screen w-full bg-white" />}>
        <SmoothScroll>
          <Suspense fallback={<div className="min-h-screen w-full bg-white" />}>
            <div className="min-h-screen w-full bg-white">
              {/* Section 1 — Hero (preloader animasi → TV → fullscreen → ratings) */}
              <HeroIntro />

              {/* Section 2 — See Menu (floating objects + tagline + giant headline) */}
              <SeeMenuSection />
            </div>
          </Suspense>

          {/* Custom scroll progress (top bar + circular pill) */}
          <ScrollProgress />
        </SmoothScroll>

        {/* Debug panel — toggle dgn Ctrl+Shift+D atau ?debug=1 */}
        <DebugPanel />
      </Suspense>
    </LayoutConfigProvider>
  );
}

export default function App() {
  return (
    <Suspense fallback={<div className="min-h-screen w-full bg-white" />}>
      <Route path="/" component={HomePage} />
      <Route path="/menu" component={MenuPage} />
      <Route path="/pawon">
        {() => <AdminPage onClose={() => window.location.href = "/"} />}
      </Route>
    </Suspense>
  );
}
